// Per-route SEO metadata. This is now a FALLBACK ONLY — the live source of
// truth is the backend (seo_settings/seo_pages tables, editable from the
// admin dashboard), fetched at runtime via fetchPublicSeo() below. These
// hardcoded values are what render if that fetch hasn't resolved yet or
// fails (offline, API down, etc.), so keep them roughly in sync.
// Parameterized routes (/events/:id, /book/:token, /referral/:refID) and the
// 404 catch-all aren't listed here; they fall back to DEFAULT_SEO.

export const SITE_NAME = 'Boomers Insurance Services';
export const SITE_URL = 'https://boomersinsuranceservices.com';
export const DEFAULT_OG_IMAGE = `${SITE_URL}/favicon.ico`;

export const DEFAULT_SEO = {
  title: 'Boomers Insurance Services',
  description: 'Boomers Insurance Services offers comprehensive Medicare, health, and wellness solutions for seniors.',
  keywords: 'medicare, insurance, seniors, health insurance, boomers',
  noindex: false,
};

export const SEO_BY_PATH = {
  '/': {
    title: 'Boomers Insurance Services | Home',
    description: 'Comprehensive Medicare plans and senior health insurance options.',
    keywords: 'medicare, insurance, senior health',
  },
  '/about': {
    title: 'About Boomers Insurance Services',
    description: 'Learn about our mission to support Medicare and senior wellness.',
    keywords: 'about us, medicare guide, company mission',
  },
  '/FindAgent': {
    title: 'Find a Local Medicare Agent | Boomers Insurance Services',
    description: 'Search for a licensed Boomers Insurance Medicare agent near you for personalized plan guidance.',
    keywords: 'find agent, medicare agent near me, local insurance agent',
  },
  '/events': {
    title: 'Upcoming Events | Boomers Insurance Services',
    description: 'Browse upcoming Medicare education events, webinars, and community meetings hosted by Boomers Insurance.',
    keywords: 'medicare events, insurance webinars, senior events',
  },
  '/locations': {
    title: 'Our Locations | Boomers Insurance Services',
    description: 'Find Boomers Insurance office locations and licensed agents serving your area.',
    keywords: 'insurance office locations, medicare offices near me',
  },
  '/FAQs': {
    title: 'Frequently Asked Questions | Boomers Insurance Services',
    description: 'Answers to common questions about Medicare plans, enrollment, and working with Boomers Insurance.',
    keywords: 'medicare faq, insurance questions, enrollment help',
  },
  '/health-care-provider': {
    title: 'Healthcare Provider Referrals | Boomers Insurance Services',
    description: 'Information for healthcare providers looking to refer patients to Boomers Insurance for Medicare guidance.',
    keywords: 'provider referral, healthcare partners, medicare referral program',
  },
  '/health-care-provider/form': {
    title: 'Submit a Provider Referral | Boomers Insurance Services',
    description: 'Healthcare providers can submit a patient referral directly to a licensed Boomers Insurance agent.',
    keywords: 'provider referral form, patient referral, medicare referral',
  },
  '/pace-lead-form': {
    title: 'PACE Program Inquiry | Boomers Insurance Services',
    description: 'Request more information about PACE (Program of All-Inclusive Care for the Elderly) eligibility and enrollment.',
    keywords: 'pace program, all-inclusive elderly care, pace eligibility',
  },
  '/medicare-intake-form': {
    title: 'Medicare Intake Form | Boomers Insurance Services',
    description: 'Start your Medicare plan review by completing our quick intake form — a licensed agent will follow up.',
    keywords: 'medicare intake, medicare plan review, get started medicare',
  },
  '/privacy-policy': {
    title: 'Privacy Policy | Boomers Insurance Services',
    description: 'Read how Boomers Insurance Services collects, uses, and protects your personal information.',
    keywords: 'privacy policy, data protection, boomers insurance privacy',
  },
  '/terms-conditions': {
    title: 'Terms & Conditions | Boomers Insurance Services',
    description: 'Review the terms and conditions governing use of the Boomers Insurance Services website and services.',
    keywords: 'terms of service, terms and conditions, legal',
  },
};

// Resolves metadata for parameterized paths too (/events/123 -> /events entry).
// `pagesByPath` (from the API, when loaded) takes priority over the static
// fallback map.
export function getSeoForPath(pathname, pagesByPath = null) {
  const lookup = (map) => {
    if (!map) return null;
    if (map[pathname]) return map[pathname];
    const base = '/' + (pathname.split('/')[1] || '');
    return map[base] || null;
  };

  return lookup(pagesByPath) || lookup(SEO_BY_PATH) || DEFAULT_SEO;
}

// Relative path so this goes through the dev proxy (src/setupProxy.js ->
// staging) in local dev, and same-origin in production.
const SEO_API_URL = '/be/public/api/v1/public/seo';

// Converts the API's flat `pages` array into the same { path: {...} } shape
// as SEO_BY_PATH, and maps snake_case DB fields to the camelCase-ish keys
// this module already uses (title/description/keywords/noindex).
export async function fetchPublicSeo() {
  const res = await fetch(SEO_API_URL);
  if (!res.ok) throw new Error(`SEO API responded ${res.status}`);
  const json = await res.json();
  if (!json.success) throw new Error('SEO API returned an error');

  const { settings, pages } = json.data;

  const pagesByPath = {};
  for (const page of pages || []) {
    pagesByPath[page.path] = {
      title: page.title,
      description: page.description,
      keywords: page.meta_keywords,
      ogImage: page.og_image,
      noindex: !page.indexing_allowed,
    };
  }

  return { settings, pagesByPath };
}
